import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useTrialHours from './hooks/useTrialHours';
import { openSupheroChat } from './supportChannels';
import { loadPackagePricing, unavailablePackagePricing } from './services/packagePricing';
import { LANDING_STYLE_HREFS } from './landingStyles';

const LANDING_SOURCE = '/premium-landing-source.html?v=20261003-100k2';
const LANDING_LOADER_MINIMUM_MS = 450;

// Landing HTML'i test suresini {{TEST_SAAT}} yer tutucusuyla tasir.
const TRIAL_HOURS_TOKEN = '{{TEST_SAAT}}';
// Yer tutucu bir kez degistirildikten sonra DOM'da kalmaz; saat sonradan guncellenirse
// tekrar yazabilmek icin dugumun ozgun metnini burada saklariz.
const originalTextByNode = new WeakMap();

const prepareLandingMarkup = (source) => {
  const bodyMatch = source.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return bodyMatch?.[1] || source;
};

export const LandingBootLoader = ({ leaving = false }) => (
  <>
    <style>{`
      @keyframes tivigoLandingBootSpin {
        to { transform: rotate(360deg); }
      }

      #tivigo-landing-boot-loader {
        transition: opacity 340ms cubic-bezier(0.2, 0.8, 0.2, 1) !important;
      }

      #tivigo-landing-boot-spinner {
        animation: tivigoLandingBootSpin 900ms linear infinite !important;
      }
    `}</style>
    <div
      id="tivigo-landing-boot-loader"
      role="status"
      aria-label="Tivigo yükleniyor"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'grid',
        minHeight: '100vh',
        placeItems: 'center',
        opacity: leaving ? 0 : 1,
        background:
          'radial-gradient(circle at 50% 48%, rgba(124, 102, 255, 0.12), transparent 25%), #080a0f',
        transition: 'opacity 340ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        pointerEvents: leaving ? 'none' : 'auto',
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'grid',
          width: 68,
          height: 68,
          placeItems: 'center',
        }}
      >
        <span
          id="tivigo-landing-boot-spinner"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            border: '3px solid rgba(255, 255, 255, 0.09)',
            borderTopColor: '#ff8a1f',
            borderRightColor: 'rgba(255, 138, 31, 0.45)',
            borderRadius: '50%',
            boxShadow: '0 0 26px rgba(255, 138, 31, 0.13)',
            animation: 'tivigoLandingBootSpin 900ms linear infinite',
          }}
        />
        <img
          src="/assets/images/tivigo-app-icon.png"
          alt=""
          style={{
            display: 'block',
            width: 42,
            height: 42,
            borderRadius: 12,
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.34)',
          }}
        />
      </div>
    </div>
  </>
);

// active=false: blog açıkken ana sayfa yok edilmez, yalnız gizlenir (PublicSite). Geri dönüşte yeniden
// kurulum, fiyat isteği ve yükleme ekranı olmaz; kaydırma konumu korunur.
const LandingPage = ({ onNavigate, active = true }) => {
  const rootRef = useRef(null);
  const navigateRef = useRef(onNavigate);
  const activeRef = useRef(active);
  const scrollRef = useRef(0);
  const activatedOnceRef = useRef(false);
  activeRef.current = active;
  const [markup, setMarkup] = useState('');
  const [markupMounted, setMarkupMounted] = useState(false);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState(null);
  const [guestCurrency, setGuestCurrency] = useState(() => {
    try { return window.sessionStorage.getItem('tivigo_guest_quote_currency') || ''; } catch { return ''; }
  });
  const trialHours = useTrialHours();
  const [baseStyleReady, setBaseStyleReady] = useState(false);
  const [dashboardStyleReady, setDashboardStyleReady] = useState(false);
  const [mobileFontStyleReady, setMobileFontStyleReady] = useState(false);
  const [mobileLayoutStyleReady, setMobileLayoutStyleReady] = useState(false);
  const [scriptInitialized, setScriptInitialized] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const [criticalAssetsReady, setCriticalAssetsReady] = useState(false);
  const [minimumDelayReady, setMinimumDelayReady] = useState(false);
  const [loaderLeaving, setLoaderLeaving] = useState(false);
  const [loaderMounted, setLoaderMounted] = useState(true);
  const stylesReady = baseStyleReady && dashboardStyleReady && mobileFontStyleReady && mobileLayoutStyleReady;
  const pageReady = markupMounted
    && stylesReady
    && scriptInitialized
    && fontsReady
    && criticalAssetsReady
    && minimumDelayReady;

  useLayoutEffect(() => {
    if (!active) return undefined;
    const body = document.body;
    const root = rootRef.current;
    const previousZoom = body.style.zoom;
    const previousRootZoom = root?.style.zoom || '';
    const previousBackground = body.style.backgroundColor;

    const applyLandingDensity = () => {
      const isDesktop = window.innerWidth >= 900;
      body.style.zoom = isDesktop ? '0.95' : '1';
      if (root) root.style.zoom = isDesktop ? '1' : '0.90';
    };

    body.classList.add('premium-landing-page');
    // Koyu zemin: geçişlerde sayfa zemini beyaza dönmesin (public/index.html'deki kural). Kaydırma çubuğu rengine dokunulmaz.
    document.documentElement.classList.add('tivigo-dark');
    body.style.backgroundColor = '#080a0f';
    applyLandingDensity();
    window.addEventListener('resize', applyLandingDensity);

    return () => {
      window.removeEventListener('resize', applyLandingDensity);
      document.documentElement.classList.remove('tivigo-dark');
      body.classList.remove('premium-landing-page');
      body.style.zoom = previousZoom;
      if (root) root.style.zoom = previousRootZoom;
      body.style.backgroundColor = previousBackground;
    };
  }, [active]);

  useLayoutEffect(() => {
    if (!active || !markupMounted || !stylesReady) return undefined;
    const header = rootRef.current?.querySelector('[data-header]');
    if (!header) return undefined;

    const documentRoot = document.documentElement;
    const previousOffset = documentRoot.style.getPropertyValue('--tivigo-landing-header-offset');
    const syncHeaderOffset = () => {
      // The visible height includes desktop/mobile zoom and the header border.
      const bottom = header.getBoundingClientRect().bottom;
      if (bottom > 0) documentRoot.style.setProperty('--tivigo-landing-header-offset', `${bottom}px`);
    };
    documentRoot.classList.add('tivigo-landing-active');
    syncHeaderOffset();
    window.addEventListener('resize', syncHeaderOffset);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(syncHeaderOffset) : null;
    observer?.observe(header);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', syncHeaderOffset);
      documentRoot.classList.remove('tivigo-landing-active');
      if (previousOffset) documentRoot.style.setProperty('--tivigo-landing-header-offset', previousOffset);
      else documentRoot.style.removeProperty('--tivigo-landing-header-offset');
    };
  }, [active, markupMounted, stylesReady]);

  // Kaydırma konumunu izle; gizliyken (display:none) tarayıcının sıfırlamasını kaydetme.
  useEffect(() => {
    const track = () => {
      if (rootRef.current && rootRef.current.offsetParent !== null) scrollRef.current = window.scrollY;
    };
    window.addEventListener('scroll', track, { passive: true });
    return () => window.removeEventListener('scroll', track);
  }, []);

  // Bloğdan geri dönünce: bağlantıda #bölüm varsa oraya, yoksa ayrıldığı kaydırma konumuna dön.
  useLayoutEffect(() => {
    if (!active) return;
    if (!activatedOnceRef.current) {
      activatedOnceRef.current = true;
      return;
    }
    const hash = window.location.hash;
    const target = hash ? rootRef.current?.querySelector(hash) : null;
    if (target) window.requestAnimationFrame(() => target.scrollIntoView({ behavior: 'auto', block: 'start' }));
    else window.scrollTo(0, scrollRef.current);
  }, [active]);

  useEffect(() => {
    navigateRef.current = onNavigate;
  }, [onNavigate]);

  useEffect(() => {
    let alive = true;
    let pending = false;
    const refresh = () => {
      // Ana sayfa gizliyken (blogdayken) fiyat isteği yapılmaz ve sayfa yeniden kurulmaz.
      if (pending || !activeRef.current) return;
      pending = true;
      loadPackagePricing({ guestCurrency }).then((catalog) => {
        if (alive) setPricing(catalog);
      }).catch(() => {
        if (alive) setPricing(unavailablePackagePricing());
      }).finally(() => { pending = false; });
    };
    refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { alive = false; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [guestCurrency]);

  useEffect(() => {
    const choose = (event) => {
      if (!['TRY', 'EUR'].includes(event.detail)) return;
      setGuestCurrency(event.detail);
      try { window.sessionStorage.setItem('tivigo_guest_quote_currency', event.detail); } catch { /* Optional persistence. */ }
    };
    window.addEventListener('tivigo:guest-currency', choose);
    return () => window.removeEventListener('tivigo:guest-currency', choose);
  }, []);

  useEffect(() => {
    let active = true;

    fetch(LANDING_SOURCE)
      .then((response) => {
        if (!response.ok) throw new Error('Landing kaynağı yüklenemedi.');
        return response.text();
      })
      .then((source) => {
        if (active) setMarkup(prepareLandingMarkup(source));
      })
      .catch((caught) => {
        if (active) setError(caught.message || 'Landing sayfası yüklenemedi.');
      });

    return () => {
      active = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (!markup || !rootRef.current) return undefined;

    const root = rootRef.current;
    root.innerHTML = markup;
    setMarkupMounted(true);

    return () => {
      root.innerHTML = '';
    };
  }, [markup]);

  // Test suresini mount edilmis DOM uzerinde yaz. Markup'i yeniden uretmek root.innerHTML'i
  // sifirlayip landing scriptlerini bozdugu icin metin dugumlerine dokunmakla yetiniyoruz.
  useEffect(() => {
    if (!markupMounted || !rootRef.current) return;

    const walker = document.createTreeWalker(rootRef.current, NodeFilter.SHOW_TEXT);
    const hours = String(trialHours);

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const original = originalTextByNode.get(node) ?? node.nodeValue;
      if (!original.includes(TRIAL_HOURS_TOKEN)) continue;
      originalTextByNode.set(node, original);
      node.nodeValue = original.split(TRIAL_HOURS_TOKEN).join(hours);
    }
  }, [markupMounted, trialHours]);

  useEffect(() => {
    const timer = window.setTimeout(() => setMinimumDelayReady(true), LANDING_LOADER_MINIMUM_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let active = true;

    const loadFont = (font) => {
      if (!document.fonts?.load) return Promise.resolve();
      return document.fonts.load(font).catch(() => []);
    };

    const fontTimeout = new Promise((resolve) => window.setTimeout(resolve, 2500));
    Promise.race([
      Promise.all([
        loadFont('700 44.7px Manrope'),
        loadFont('500 16px Inter'),
      ]),
      fontTimeout,
    ]).finally(() => {
      if (active) setFontsReady(true);
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const decodeImage = (src) => new Promise((resolve) => {
      const image = new Image();
      const done = () => resolve();
      image.onload = done;
      image.onerror = done;
      image.src = src;
      if (image.decode) image.decode().then(done).catch(done);
    });

    Promise.all([
      decodeImage('/assets/images/tivigo-wordmark.png'),
      decodeImage('/assets/images/product/windows-home.jpg'),
    ]).finally(() => {
      if (active) setCriticalAssetsReady(true);
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!markupMounted || !stylesReady || !pricing || !rootRef.current) return undefined;

    const root = rootRef.current;
    const handleLandingClick = (event) => {
      const chatLink = event.target.closest('[data-suphero-chat-open]');
      if (chatLink) {
        event.preventDefault();
        openSupheroChat();
        return;
      }

      const demoLink = event.target.closest('[data-demo-link]');
      if (demoLink) {
        event.preventDefault();
        return;
      }

      const routeLink = event.target.closest('[data-tivigo-route]');
      if (!routeLink || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const route = routeLink.dataset.tivigoRoute;
      if (!route || !route.startsWith('/') || !navigateRef.current) return;

      event.preventDefault();
      navigateRef.current(route);
    };

    const handleLandingInitialized = () => setScriptInitialized(true);

    root.addEventListener('click', handleLandingClick);
    window.addEventListener('premium-landing:initialized', handleLandingInitialized, { once: true });

    const previousScript = document.querySelector('script[data-premium-landing]');
    previousScript?.remove();

    window.tivigoPackagePricing = pricing;
    const script = document.createElement('script');
    script.src = '/premium-landing.js?v=20260920-region-choice';
    script.dataset.premiumLanding = 'true';
    script.onerror = () => setError('Landing etkileşimleri yüklenemedi.');
    document.body.appendChild(script);

    return () => {
      root.removeEventListener('click', handleLandingClick);
      window.removeEventListener('premium-landing:initialized', handleLandingInitialized);
      window.__premiumLandingStatsPlacementCleanup?.();
      delete window.__premiumLandingStatsPlacementCleanup;
      window.__premiumLandingMobileUxCleanup?.();
      delete window.__premiumLandingMobileUxCleanup;
      script.remove();
      document.body.classList.remove('menu-open');
    };
  }, [markupMounted, stylesReady, pricing]);

  useEffect(() => {
    if (!pageReady) return undefined;

    const frame = window.requestAnimationFrame(() => setLoaderLeaving(true));
    const timer = window.setTimeout(() => setLoaderMounted(false), 380);
    const heroTimer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('premium-landing:reveal-hero'));
    }, 520);
    // Blogdan "/#pricing" gibi bir bağlantıyla gelindiyse sayfa hazır olunca o bölüme in.
    const hash = window.location.hash;
    const hashTimer = hash && rootRef.current?.querySelector(hash)
      ? window.setTimeout(() => rootRef.current?.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 420)
      : null;

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.clearTimeout(heroTimer);
      if (hashTimer !== null) window.clearTimeout(hashTimer);
    };
  }, [pageReady]);

  if (error) {
    return (
      <main className="premium-landing-status" hidden={!active}>
        <img src="/assets/images/tivigo-wordmark.png" alt="Tivigo" />
        <p>{error}</p>
        <button type="button" onClick={() => window.location.reload()}>Tekrar dene</button>
      </main>
    );
  }

  return (
    <>
      <link
        rel="stylesheet"
        href={LANDING_STYLE_HREFS.base}
        onLoad={() => setBaseStyleReady(true)}
        onError={() => setError('Landing ana stili yüklenemedi.')}
      />
      <link
        rel="stylesheet"
        href={LANDING_STYLE_HREFS.dashboard}
        onLoad={() => setDashboardStyleReady(true)}
        onError={() => setError('Landing tema stili yüklenemedi.')}
      />
      <link
        rel="stylesheet"
        href={LANDING_STYLE_HREFS.mobileFonts}
        onLoad={() => setMobileFontStyleReady(true)}
        onError={() => setError('Landing mobil yazı stili yüklenemedi.')}
      />
      <link
        rel="stylesheet"
        href={LANDING_STYLE_HREFS.mobileLayout}
        onLoad={() => setMobileLayoutStyleReady(true)}
        onError={() => setError('Landing mobil yerleşim stili yüklenemedi.')}
      />
      {loaderMounted && active && <LandingBootLoader leaving={loaderLeaving} />}
      <div
        ref={rootRef}
        className="premium-landing-react-root"
        aria-busy={!pageReady}
        aria-hidden={!active}
        style={{
          display: active ? 'block' : 'none',
          minHeight: '100vh',
          visibility: pageReady ? 'visible' : 'hidden',
          opacity: pageReady ? 1 : 0,
          background: '#080a0f',
          transition: 'opacity 320ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      />
    </>
  );
};

export default LandingPage;
